import type { SelectedFacetParam } from "common/types";

import { DEFAULT_LIST_LEADS_SEARCH_PARAMS } from "../constants";

import type { ListLeadsSearchParams } from "../types";

type SearchParamKey = keyof ListLeadsSearchParams<SelectedFacetParam>;
type FacetSearchParamKey = Exclude<SearchParamKey, "keyword">;

export default function useListLeadsParams(
  searchParams: ListLeadsSearchParams<SelectedFacetParam>,
  setSearchParams: (
    searchParams: ListLeadsSearchParams<SelectedFacetParam>,
  ) => void,
) {
  function toggleSearchParam(
    key: FacetSearchParamKey,
    value: SelectedFacetParam,
  ) {
    const selection = searchParams[key];

    setSearchParams({
      ...searchParams,
      [key]: selection.some(({ id }) => id === value.id)
        ? selection.filter(({ id }) => id !== value.id)
        : //only the id and name are stored, the facet count belongs to the response
          [...selection, { id: value.id, name: value.name }],
    });
  }

  function setSearchParam<T extends SearchParamKey>(
    key: T,
    value: ListLeadsSearchParams<SelectedFacetParam>[T],
  ) {
    setSearchParams({ ...searchParams, [key]: value });
  }

  function resetSearchParam<T extends SearchParamKey>(key: T) {
    setSearchParam(key, DEFAULT_LIST_LEADS_SEARCH_PARAMS[key]);
  }

  function resetFilters() {
    setSearchParams({
      ...DEFAULT_LIST_LEADS_SEARCH_PARAMS,
      keyword: searchParams.keyword,
    });
  }

  function resetParams() {
    setSearchParams(DEFAULT_LIST_LEADS_SEARCH_PARAMS);
  }

  //the keyword has its own search input, so it doesn't count as a filter
  const { keyword: _, ...filters } = searchParams;
  const hasFilters = Object.values(filters).some(({ length }) => !!length);

  return {
    toggleSearchParam,
    setSearchParam,
    resetSearchParam,
    resetFilters,
    resetParams,
    hasFilters,
  };
}
